import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Field } from "@formily/react";
import { createColumnBuilder } from "../../../table/columnBuilder";
import { type TargetQueryV4Row } from "../../../../modules/perf_reports/api/target/v4TargetReportByTarget";
import { useBaseTargetColumns } from "../../../../modules/perf_reports/target_report/useBaseTargetColumns";
import { TargetTextCell } from "../../../../modules/perf_reports/target_report/TargetTextCell";
import { useAppSelector } from "../../../../redux/hooks";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { useInitialValueSync } from "../../../../utils/formily";
import { CurrencyInput } from "../../../fields/formily/NumberInput";
import { formatCurrency } from "../../../../utils/formatNumber";
import { css } from "../../../../../styled-system/css";
import { getMinBid } from "../../../core/blocks/AdPlan/constants";
import { FaArrowUp, FaArrowDown, FaEquals, FaCircleExclamation } from "react-icons/fa6";
import { HoverInfo } from "../../../core/basic/Tooltip";
import type { SimulationRow } from "../../../../zodios/apiDefinitions/api/bidSimulation";

const columnHelper = createColumnBuilder<TargetQueryV4Row>();
const noValueClass = css({ color: "muted", textAlign: "center" });

type BidTrend = "Increasing" | "Decreasing" | "Stable";

const BID_TREND_ICON: Record<BidTrend, React.JSX.Element> = {
  Increasing: <FaArrowUp />,
  Decreasing: <FaArrowDown />,
  Stable: <FaEquals />,
};

type UseAdTargetColumnsOptions = {
  targetType: "keyword" | "target" | "negative-keyword";
  adType?: number;
  isNTMAd: boolean;
  simulationData?: Map<string, SimulationRow>;
  showPreviewBid?: boolean;
};

export const useAdTargetColumns = ({
  targetType,
  adType,
  isNTMAd,
  simulationData,
  showPreviewBid = false,
}: UseAdTargetColumnsOptions) => {
  const { t } = useTranslation(["advertising"]);
  const isNegativeKeyword = targetType === "negative-keyword";
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const tagColumnId = tagsEnhancedEnabled ? "tags_enhanced.id" : "tags";

  const translateBidTrend = useCallback(
    (trend: BidTrend) => {
      switch (trend) {
        case "Increasing":
          return t(($) => $.advertising["Increasing"]);
        case "Decreasing":
          return t(($) => $.advertising["Decreasing"]);
        case "Stable":
          return t(($) => $.advertising["Stable"]);
      }
    },
    [t],
  );
  const queryTargetType = isNegativeKeyword ? "keyword" : targetType;

  const minBid = getMinBid(adType);

  const restColumns = useBaseTargetColumns(queryTargetType);

  const columns = useMemo(() => {
    // Ad-context text column: custom header, no ad link
    const textColumn = columnHelper.accessor("text", {
      id: "text",
      header:
        isNegativeKeyword ? t(($) => $.advertising["Negative Keyword"])
        : targetType === "keyword" ? t(($) => $.advertising["Keyword"])
        : t(($) => $.advertising["Target"]),
      enableHiding: false,
      minSize: 100,
      size: 300,
      cell: (props) => <TargetTextCell target={props.row.original} />,
    });

    const hasSimulation = simulationData !== undefined;

    const bidColumn = columnHelper.accessor("bid_resolved", {
      id: "bid_resolved",
      header: t(($) => $.advertising["Bid"]),
      size: 90,
      cell:
        hasSimulation ?
          function BidCell({ getValue }) {
            const mp = useAppSelector((state) => state.marketPlace.marketPlace);
            return formatCurrency(getValue(), { marketPlace: mp, decimalPlaces: 2 });
          }
        : function BidCell({ getValue, column }) {
            const mp = useAppSelector((state) => state.marketPlace.marketPlace);
            useInitialValueSync(column.id, getValue());
            return (
              <Field
                name={`${column.id}`}
                initialValue={getValue()}
                validator={(value: number) => {
                  if (Number.isNaN(value)) {
                    return t(($) => $.advertising["Enter a bid value"]);
                  }
                  if (value < minBid) {
                    return t(($) => $.advertising["Bid must be at least {minBid}"], {
                      minBid: formatCurrency(minBid, {
                        marketPlace: mp,
                        decimalPlaces: 2,
                      }),
                    });
                  }
                }}
                component={[
                  CurrencyInput,
                  {
                    variant: "inline",
                    css: css.raw({ marginX: "small" }),
                  },
                ]}
              />
            );
          },
      meta: {
        columnFilterOptions: { format: "currency" },
        columnSelectorOptions: {
          grouping: "configuration",
          description: t(($) => $.advertising["The current bid for the advertised {targetType}"], { targetType }),
        },
        justify: "center" as const,
      },
    });

    const simulatedBidColumn =
      hasSimulation ?
        columnHelper.display({
          id: "simulated_bid",
          header: t(($) => $.advertising["Simulated Bid"]),
          size: 110,
          cell: function SimulatedBidCell({ row }) {
            const mp = useAppSelector((state) => state.marketPlace.marketPlace);
            const simRow = simulationData.get(row.id);
            if (!simRow) return <span className={noValueClass}>—</span>;
            return formatCurrency(simRow.new_bid, { marketPlace: mp, decimalPlaces: 2 });
          },
          meta: { justify: "center" as const },
        })
      : null;

    // Bid Trellis would set on an imported ad, shown until the org accepts Trellis' bids
    const previewBidColumn =
      showPreviewBid ?
        columnHelper.accessor("preview_bid", {
          id: "preview_bid",
          header: t(($) => $.advertising["Bid Preview"]),
          size: 110,
          cell: function PreviewBidCell({ getValue, row }) {
            const mp = useAppSelector((state) => state.marketPlace.marketPlace);
            const previewBid = getValue();
            if (previewBid == null) {
              return <span className={noValueClass}>—</span>;
            }
            const currentBid = row.original.bid_resolved;
            const tooHigh = previewBid > currentBid * 1.5;
            const tooLow = previewBid < currentBid * 0.5;
            return (
              <>
                {(tooHigh || tooLow) && (
                  <HoverInfo iconCss={{ color: "error", paddingRight: "small" }} icon={<FaCircleExclamation />}>
                    {tooHigh ?
                      t(($) => $.advertising["Bid preview is significantly higher than the current bid"])
                    : t(($) => $.advertising["Bid preview is significantly lower than the current bid"])}
                  </HoverInfo>
                )}
                <span>{formatCurrency(previewBid, { marketPlace: mp, decimalPlaces: 2 })}</span>
              </>
            );
          },
          // Read-only like the legacy column: the v4 report returns the value but is not asked to
          // order or filter on it.
          enableSorting: false,
          meta: {
            columnSelectorOptions: {
              grouping: "configuration",
              description: t(($) => $.advertising["The bid Trellis would set for this {targetType}"], { targetType }),
            },
            justify: "center" as const,
          },
        })
      : null;

    // Bid trend column
    const bidTrendColumn = columnHelper.accessor("bid_trend_latest", {
      id: "bid_trend_latest",
      header: t(($) => $.advertising["Bid Trend"]),
      minSize: 70,
      maxSize: 70,
      cell: ({ getValue }) => {
        const trend = getValue();

        return (
          <HoverInfo
            iconCss={{
              borderRadius: "pill",
              padding: "small",
              color: "trellisPalette.16",
              fontSize: "small",
              fontWeight: "bold",
              textTransform: "none",
              backgroundColor: "trellisPalette.16/12",
              _hover: { backgroundColor: "trellisPalette.16/24" },
            }}
            icon={BID_TREND_ICON[trend]}
          >
            {translateBidTrend(trend)}
          </HoverInfo>
        );
      },
      meta: {
        justify: "center",
        columnFilterOptions: {
          format: "select",
        },
        columnSelectorOptions: {
          grouping: "configuration",
          description: t(($) => $.advertising["Latest bid trend direction"]),
        },
      },
    });

    // Invisible column to support filtering by tag
    const tagsColumn = columnHelper.accessor((row) => row.tags, {
      id: tagColumnId,
      header: t(($) => $.advertising["Tags"]),
      enableHiding: false,
      meta: {
        columnFilterOptions: {
          format: "multi-select",
        },
      },
    });

    const hasFixedBidColumn = columnHelper.accessor((row) => row.has_manual_bid, {
      id: "has_manual_bid",
      header: t(($) => $.advertising["Fixed Bid"]),
      enableHiding: false,
      meta: {
        columnFilterOptions: {
          format: "single-select",
        },
      },
    });

    return isNegativeKeyword ?
        [textColumn, tagsColumn, ...restColumns]
      : [
          textColumn,
          bidColumn,
          previewBidColumn,
          simulatedBidColumn,
          bidTrendColumn,
          hasFixedBidColumn,
          tagsColumn,
          ...restColumns,
        ].filter(Boolean);
  }, [
    restColumns,
    isNegativeKeyword,
    targetType,
    minBid,
    simulationData,
    showPreviewBid,
    tagColumnId,
    t,
    translateBidTrend,
  ]);

  const initialVisibility: Record<string, boolean> = {
    ad_group_name: isNTMAd,
    bid_trend_latest: false,
    creation_date: false,
    state: false,
    target_bid_state: false,
    has_manual_bid: false,
    [tagColumnId]: false,
  };

  return { columns, initialVisibility };
};
